import { DialogComponent } from '@theme/dialog';

/* ======================================================
   INTERCEPT CART ADD
====================================================== */
(function interceptCartAdd() {
  if (window.__cartInterceptActive) return;
  window.__cartInterceptActive = true;

  let cartCountBeforeAdd = 0;

  async function getCartCount() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await res.json();
      return cart?.item_count || 0;
    } catch {
      return 0;
    }
  }

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const [url, options] = args;

    if (
      typeof url === 'string' &&
      url.includes('/cart/add') &&
      (!options || options.method === 'POST')
    ) {
      cartCountBeforeAdd = await getCartCount();
    }

    const response = await originalFetch(...args);

    try {
      if (
        typeof url === 'string' &&
        url.includes('/cart/add') &&
        (!options || options.method === 'POST')
      ) {
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: { previousCount: cartCountBeforeAdd }
        }));
      }
    } catch {}

    return response;
  };

  // XMLHttpRequest intercept
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._cartUrl = url;
    this._cartMethod = method;
    return originalXHROpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = async function(...args) {
    if (
      this._cartMethod === 'POST' &&
      typeof this._cartUrl === 'string' &&
      this._cartUrl.includes('/cart/add')
    ) {
      const prevCount = await getCartCount();
      this.addEventListener('load', () => {
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: { previousCount: prevCount }
        }));
      });
    }
    return originalXHRSend.apply(this, args);
  };
})();

/* ======================================================
   CART DRAWER COMPONENT
====================================================== */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.dialog = this.querySelector('dialog.cart-drawer__dialog');
    if (!this.dialog) {
      this.dialog = this.querySelector('dialog');
    }

    this.handleCartAdd = this.handleCartAdd.bind(this);
    document.addEventListener('cart:add', this.handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:add', this.handleCartAdd);
  }

  open() {
    const cartDialog = this.querySelector('dialog.cart-drawer__dialog') || this.querySelector('dialog');
    if (!cartDialog) return;

    if (typeof cartDialog.showModal === 'function') {
      if (!cartDialog.open) cartDialog.showModal();
    } else {
      cartDialog.setAttribute('open', '');
    }
  }

  close() {
    const cartDialog = this.querySelector('dialog.cart-drawer__dialog') || this.querySelector('dialog');
    if (!cartDialog) return;

    if (typeof cartDialog.close === 'function') {
      cartDialog.close();
    } else {
      cartDialog.removeAttribute('open');
    }
  }

  /* ====================================================
     HANDLE CART ADD EVENT
  ==================================================== */
  async handleCartAdd(event) {
    const previousCount = event?.detail?.previousCount || 0;

    // Wait for cart to update
    await this.waitForCartUpdate(previousCount);

    // Refresh drawer
    await this.refreshDrawer();

    // Open
    this.open();
  }

  /* ====================================================
     WAIT FOR CART COUNT TO INCREASE
  ==================================================== */
  async waitForCartUpdate(previousCount = 0) {
    const start = Date.now();

    while (Date.now() - start < 3000) {
      try {
        const res = await fetch('/cart.js', { 
          credentials: 'same-origin',
          cache: 'no-store'
        });
        const cart = await res.json();

        if (cart?.item_count > previousCount) {
          return true;
        }
      } catch {}

      await new Promise(resolve => setTimeout(resolve, 150));
    }
    return false;
  }

  /* ====================================================
     REFRESH DRAWER — FETCH FULL PAGE
  ==================================================== */
  async refreshDrawer() {
    const cartDialog = this.querySelector('dialog.cart-drawer__dialog') || this.querySelector('dialog');
    if (!cartDialog) return;

    try {
      const cacheBuster = Date.now();
      
      // Fetch the actual page (not sections API)
      const res = await fetch(`/?_=${cacheBuster}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store'
        }
      });

      if (!res.ok) {
        console.error('[CartDrawer] Fetch failed:', res.status);
        return;
      }

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      // Find the cart drawer in the fetched page
      const newDrawer = doc.querySelector('cart-drawer-component');
      const newDialog = newDrawer?.querySelector('dialog.cart-drawer__dialog') || 
                        newDrawer?.querySelector('dialog');

      if (newDialog) {
        // Replace entire dialog content
        cartDialog.innerHTML = newDialog.innerHTML;
        
        // Copy classes
        cartDialog.className = newDialog.className;
      } else {
        console.error('[CartDrawer] No dialog found in page refresh');
      }
    } catch (err) {
      console.error('[CartDrawer] refresh failed:', err);
    }
  }
}

/* ======================================================
   DEFINE ELEMENT
====================================================== */
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}